import React, { useState, useEffect } from "react";
import { useUser } from "../../../context/UserContext";
import { dateToUnixTimestamp } from "../../../util_functions/helpers";

import config from "../../../config/config";
import MeetAndGreetInvitationContent from "./MGInvitationContent";
import axios from "axios";

const MeetAndGreet = ({
  userId,
  userRole,
  onClose,
  selectedThread,
  jobTitle,
  messageId,
  jobId,
  onScheduleSet,
}) => {

  console.log("MeetAndGreet props:", {
    userId,
    userRole,
    onClose,
    selectedThread,
    jobTitle,
    messageId,
    jobId,
    onScheduleSet,
  });

  const { fetchClientUserById, fetchWorkerUserById } = useUser();
  const [date, setDate] = useState({ day: "", month: "", year: "" });
  const [time, setTime] = useState({ hours: "", minutes: "", ampm: "AM" });
  const [preferredMethod, setPreferredMethod] = useState("In-app phone");
  const [duration, setDuration] = useState("");
  const [selectedUserEmail, setSelectedUserEmail] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const now = dateToUnixTimestamp(new Date());

  const baseURL = config.API_BASE_URL;

  useEffect(() => {
    if (!selectedThread) return;

    const loadEmail = async () => {
      try {
        let response;

        if (userRole === "worker") {
          response = await axios.get(
            `${config.API_BASE_URL}/v1/client-users/${selectedThread.clientId}`
          );
        } else {
          response = await axios.get(
            `${config.API_BASE_URL}/v1/worker-users/${selectedThread.workerId}`
          );
        }

        const userObj = response.data;
        if (userObj?.email) {
          setSelectedUserEmail(userObj.email);
          console.log("Fetched user email:", userObj.email);
        } else {
          console.warn("No email found on fetched user:", userObj);
        }
      } catch (err) {
        console.error("Failed to fetch counterpart email:", err);
      }
    };

    loadEmail();
  }, [selectedThread, userRole]);

  function parseDuration(duration) {
    switch (duration) {
      case "30 minutes": return 30 * 60 * 1000;
      case "1 hour": return 60 * 60 * 1000;
      case "1 hour 30 minutes": return 90 * 60 * 1000;
      case "2 hours": return 120 * 60 * 1000;
      default: return 60 * 60 * 1000;
    }
  }

  function formatForGoogle(dt) {
    return dt
      .toISOString()
      .replace(/[-:.]/g, "")
      .replace(/(\d{8}T\d{6})\d{3}Z$/, "$1Z");
  }

  function createGoogleCalendarLink(details) {
    const pad2 = v => String(v).padStart(2, "0");

    const year = Number(details.date.year);
    const month = Number(details.date.month) - 1;
    const day = Number(details.date.day);

    let hour24 = Number(details.time.hours) % 12;
    if (details.time.ampm === "PM") hour24 += 12;
    const minute = Number(details.time.minutes);

    const start = new Date(year, month, day, hour24, minute);
    const end = new Date(start.getTime() + parseDuration(details.duration));

    const startParam = formatForGoogle(start);
    const endParam = formatForGoogle(end);

    const title = encodeURIComponent(`Meet and Greet Session with ${details.schedulerName}`);
    const detailsQ = encodeURIComponent(
      `Job Title : ${details.jobTitle}`
    );
    const location = encodeURIComponent(details.preferredMethod);

    return (
      `https://calendar.google.com/calendar/render` +
      `?action=TEMPLATE` +
      `&text=${title}` +
      `&dates=${startParam}/${endParam}` +
      `&details=${detailsQ}` +
      `&location=${location}`
    );
  }

  function createOutlookCalendarLink(details) {
    const pad2 = v => String(v).padStart(2, "0");

    const year = Number(details.date.year);
    const month = Number(details.date.month) - 1;
    const day = Number(details.date.day);

    let hour24 = Number(details.time.hours) % 12;
    if (details.time.ampm === "PM") hour24 += 12;
    const minute = Number(details.time.minutes);

    const start = new Date(year, month, day, hour24, minute);
    const end = new Date(start.getTime() + parseDuration(details.duration));

    const title = encodeURIComponent(`Meet and Greet Session with ${details.schedulerName}`);
    const bodyText = encodeURIComponent(
      `Job Title : ${details.jobTitle}`
    );
    const location = encodeURIComponent(details.preferredMethod);

    return (
      `https://outlook.live.com/calendar/0/deeplink/compose` +
      `?path=/calendar/action/compose` +
      `&startdt=${start.toISOString()}` +
      `&enddt=${end.toISOString()}` +
      `&subject=${title}` +
      `&body=${bodyText}` +
      `&location=${location}`
    );
  }

  const sendInvitation = () => {
    setErrorMessage("");
    const formattedDate = `${String(date.month).padStart(2, '0')}/${String(date.day).padStart(2, '0')}/${date.year}`;
    const formattedTime = `${time.hours}:${String(time.minutes).padStart(2, '0')} ${time.ampm}`;
    const recipientName = userRole === "worker"
      ? selectedThread.client
      : selectedThread.worker;

    const schedulerName = userRole === "worker"
    ? selectedThread.worker   
    : selectedThread.client;

    const details = {
      userId: selectedThread.id,
      schedulerName: schedulerName,
      recipientFullName: recipientName,
      jobTitle,
      date,
      time,
      preferredMethod,
      duration,
      jobId,
    };

    const htmlContent = MeetAndGreetInvitationContent({
      ...details,
      googleLink: createGoogleCalendarLink(details),
      outlookLink: createOutlookCalendarLink(details),
      messageId,
    });

    axios
      .post(`${baseURL}/v1/notifications/send-invite`, {
        to: selectedUserEmail,
        subject: "Meet and Greet Session",
        htmlContent,
      })
      .then(() => {
        const patchPayload = {
          isMeetAndGreetSet: true,
          meetingSetBy: `${userRole}`,
          meetAndGreetSetTimestamp: now,
          meetAndGreetDetails: {
            meetAndGreetDate: formattedDate,
            meetAndGreetTime: formattedTime,
            meetAndGreetDuration: duration,
            meetAndGreetPreferredMethod: preferredMethod,
            isPhoneCallCompleted: false,
            isVideoCallCompleted: false,
            isInPersonCompleted: false,
          },
        };

        console.log("PATCH payload for send-invite:", patchPayload);
        axios
          .patch(`${baseURL}/v1/messages/send-invite/${messageId}`, patchPayload)
          .then(() => {
            setSuccessMessage("Invitation successfully sent!");
            onScheduleSet({ date, time, duration, preferredMethod });
          })
          .catch((error) => {
            setErrorMessage(
              "Failed to update message status. Please try again.",
              error
            );
          });
      })
      .catch((error) => {
        setErrorMessage("Failed to send invitation. Please try again.", error);
      });
  };

  return (
    <div className="mg-modal-overlay">
      <div
        className="meet-greet-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mg-close-button" onClick={onClose}>
          X
        </div>
        <div className="mg-date-time-container">
          <div className="mg-date-section">
            <h3>Please select Date and Time for the meeting</h3>
            <div className="mg-date-selects">
              <div className="label-select-container">
                <label>Day</label>
                <select
                  value={date.day}
                  onChange={(e) =>
                    setDate((prev) => ({ ...prev, day: e.target.value }))
                  }
                >
                  <option value="">Please Select</option>
                  {[...Array(31)].map((_, i) => (
                    <option key={i} value={i + 1}>
                      {i + 1}
                    </option>
                  ))}
                </select>
              </div>
              <div className="label-select-container">
                <label>Month</label>
                <select
                  value={date.month}
                  onChange={(e) =>
                    setDate((prev) => ({ ...prev, month: e.target.value }))
                  }
                >
                  <option value="">Please Select</option>
                  {[
                    "January",
                    "February",
                    "March",
                    "April",
                    "May",
                    "June",
                    "July",
                    "August",
                    "September",
                    "October",
                    "November",
                    "December",
                  ].map((month, i) => (
                    <option key={i} value={i + 1}>
                      {month}
                    </option>
                  ))}
                </select>
              </div>
              <div className="label-select-container">
                <label>Year</label>
                <select
                  value={date.year}
                  onChange={(e) =>
                    setDate((prev) => ({ ...prev, year: e.target.value }))
                  }
                >
                  <option value="">Please Select</option>
                  {[...Array(10)].map((_, i) => (
                    <option key={i} value={2025 + i}>
                      {2025 + i}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="mg-time-section">
            <div className="time-selects">
              <div className="label-select-container">
                <label>Hours</label>
                <select
                  value={time.hours}
                  onChange={(e) =>
                    setTime((prev) => ({ ...prev, hours: e.target.value }))
                  }
                >
                  <option value="">Please Select</option>
                  {[...Array(12)].map((_, i) => (
                    <option key={i} value={i + 1}>
                      {i + 1}
                    </option>
                  ))}
                </select>
              </div>
              <div className="label-select-container">
                <label>Minutes</label>
                <select
                  value={time.minutes}
                  onChange={(e) =>
                    setTime((prev) => ({ ...prev, minutes: e.target.value }))
                  }
                >
                  <option value="">Please Select</option>
                  {[...Array(60)].map((_, i) => (
                    <option key={i} value={i}>
                      {i.toString().padStart(2, "0")}
                    </option>
                  ))}
                </select>
              </div>
              <div className="label-select-container">
                <label>AM/PM</label>
                <select
                  value={time.ampm}
                  onChange={(e) =>
                    setTime((prev) => ({ ...prev, ampm: e.target.value }))
                  }
                >
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>
              <div className="label-select-container">
                <label>Duration</label>
                <select
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                >
                  <option value="">Please Select</option>
                  <option value="15 minutes">15 minutes</option>
                  <option value="30 minutes">30 minutes</option>
                </select>
              </div>
            </div>
          </div>
          <div className="mg-date-section-pref">
            <h3>Preferred communication method</h3>
            <div className="label-select-container">
              <select
                value={preferredMethod}
                onChange={(e) => setPreferredMethod(e.target.value)}
              >
                <option value="phone">In-app phone (recommended)</option>
                <option value="video">Video call (recommended)</option>
                <option value="in-person">In person</option>
              </select>
            </div>
          </div>
        </div>
        {successMessage && (
          <div className="invite-success-message">{successMessage}</div>
        )}
        <div className="button-container">
          {errorMessage && (
            <div className="invite-error-message">{errorMessage}</div>
          )}
          <button onClick={sendInvitation}>Send Invite</button>
        </div>
      </div>
    </div>
  );
};

export default MeetAndGreet;
